import { useEffect, useRef, useState } from 'react';
import { ImageIcon, PlusIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  validateImageFile,
  DEFAULT_IMAGE_ACCEPT,
  DEFAULT_IMAGE_MAX_SIZE_MB,
} from '@/shared/lib/validate-image-file';
import { PositiveNegativeButtons } from './positive-negative-buttons';

/**
 * UploadImageDialog — Component (L1)
 *
 * Image uploader. Single entry point for any image-attachment field:
 * patient photo, doctor photo, staff photo, organisation logo, insurance
 * card photos.
 *
 * Two modes:
 *   - empty   → renders a dashed "+ add" tile that opens a native file
 *               picker on click.
 *   - filled  → renders a thumbnail of the current image; clicking it
 *               opens a large preview dialog with Change / Remove / Close.
 *
 * Client-side validation per `accept` (MIME types) + `maxSizeMB`. Invalid
 * files surface an inline error and don't fire `onChange`.
 *
 * Lifecycle of `value`:
 *   - Parent owns the persisted URL (signed CDN URL, etc.).
 *   - On upload, this component fires `onChange(file)` with the raw `File`.
 *     Parent uploads it to S3/CDN and writes the resulting URL back into
 *     `value`.
 *   - On remove, fires `onChange(null)`.
 *   - While the parent is uploading, this component shows a transient
 *     `URL.createObjectURL` preview of the just-picked file so users see
 *     immediate feedback (no flash to empty between local-pick and
 *     remote-URL).
 *
 * a11y: thumbnail is `role=button` with descriptive aria-label; preview
 * dialog traps focus + closes on Escape (shadcn Dialog handles this).
 *
 * Action ordering follows the canonical Cancel-LEFT / Primary-RIGHT rule
 * (see PositiveNegativeButtons).
 *
 * Scope: cross-module · Surfaces: practice · practitioner · patient
 * Contract: scrips-signal-ds/contracts/UploadImageDialog.json
 */

export interface UploadImageDialogProps {
  /** Caption above the thumbnail. Doubles as the accessible name. */
  label: string;
  /** Current image URL (signed CDN URL) or null when empty. */
  value: string | null;
  /**
   * Called with the picked `File` on upload, or `null` on remove.
   * Parent handles the upload-to-CDN and re-writes `value` with the new URL.
   */
  onChange: (file: File | null) => void;
  /** Comma-separated MIME types for the native file input `accept` attribute. */
  accept?: string;
  /** Max file size in megabytes. Validated client-side before `onChange`. */
  maxSizeMB?: number;
  /**
   * Suppress the Remove affordance inside the preview dialog. Used when a
   * consumer wants pure replace-only semantics — currently the organisation
   * clinic-license front/back tiles (`account-details-page.tsx`) match
   * Flutter's `license_photo_widget.dart` which ships `removeBtn: Container()`.
   * Default `false`. With this on, `onChange(null)` is never fired.
   */
  hideRemove?: boolean;
  /**
   * Render the tile in read-only mode — no file picker, no Change /
   * Remove buttons in the preview, no `onChange` calls. Used when the
   * consumer's edit permission is denied (e.g. Staff Personal tab for
   * a non-admin viewer). When `value` is set, the thumbnail still opens
   * a preview-only dialog (Close button only). When `value` is null,
   * the empty-state placeholder stays static (no "+ add" affordance).
   * Default `false`. Added per Samer review on PR #350 (PROD-789) to
   * preserve the read-only contract of the old `PhotoSlot` widget that
   * this component replaced.
   */
  readOnly?: boolean;
  className?: string;
}

export function UploadImageDialog({
  label,
  value,
  onChange,
  accept = DEFAULT_IMAGE_ACCEPT,
  maxSizeMB = DEFAULT_IMAGE_MAX_SIZE_MB,
  hideRemove = false,
  readOnly = false,
  className,
}: UploadImageDialogProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null);

  // Tear down any blob: URL we created so we don't leak memory.
  useEffect(() => {
    return () => {
      if (localPreviewUrl) URL.revokeObjectURL(localPreviewUrl);
    };
  }, [localPreviewUrl]);

  // Whenever the parent commits a new persisted URL, drop the stale local blob
  // preview by adjusting state *during render* rather than in an effect — avoids
  // the synchronous-setState-in-effect cascading-render warning. The teardown
  // effect above revokes the old blob: URL when `localPreviewUrl` changes to
  // null, so there's no leak. Converges in one render (the guard goes false
  // once `localPreviewUrl` is null).
  if (value && localPreviewUrl) {
    setLocalPreviewUrl(null);
  }

  const displayUrl = value ?? localPreviewUrl;

  const openFilePicker = () => {
    setError(null);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file twice still fires change.
    e.target.value = '';
    if (!file) return;

    const validationError = validateImageFile(file, { accept, maxSizeMB });
    if (validationError) {
      setError(validationError);
      return;
    }

    setError(null);
    // Show local preview immediately while parent uploads to CDN.
    const blobUrl = URL.createObjectURL(file);
    setLocalPreviewUrl(blobUrl);
    onChange(file);
    setPreviewOpen(false);
  };

  const handleRemove = () => {
    setError(null);
    if (localPreviewUrl) {
      URL.revokeObjectURL(localPreviewUrl);
      setLocalPreviewUrl(null);
    }
    onChange(null);
    setPreviewOpen(false);
  };

  return (
    <div className={cn('inline-block', className)}>
      <span className="block text-sm font-medium text-text-secondary mb-1">{label}</span>

      {displayUrl ? (
        <button
          type="button"
          onClick={() => setPreviewOpen(true)}
          aria-label={`Preview ${label}`}
          className="relative size-20 overflow-hidden rounded-md border border-border bg-surface-card transition hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
        >
          <img
            src={displayUrl}
            alt={label}
            className="h-full w-full object-cover"
          />
        </button>
      ) : readOnly ? (
        // Read-only empty state — preserve the visual placeholder shape
        // but no "+ add" affordance. Matches the old PhotoSlot's "No photo"
        // state for users without edit permission.
        <div
          aria-label={`${label} (read-only)`}
          className="flex size-20 flex-col items-center justify-center rounded-md border border-dashed border-border bg-surface-card text-text-secondary"
        >
          <ImageIcon className="size-5" aria-hidden />
        </div>
      ) : (
        <button
          type="button"
          onClick={openFilePicker}
          aria-label={`Upload ${label}`}
          className="flex size-20 flex-col items-center justify-center rounded-md border-2 border-dashed border-border bg-surface-card text-text-secondary transition hover:border-blue-500 hover:text-blue-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500 focus-visible:outline-offset-2"
        >
          <PlusIcon className="size-5" aria-hidden />
          <span className="mt-1 text-xs">Add</span>
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileChange}
        className="sr-only"
        aria-hidden
        tabIndex={-1}
      />

      {error && (
        <p role="alert" className="mt-1 max-w-[16rem] text-sm text-destructive">
          {error}
        </p>
      )}

      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
          </DialogHeader>
          {displayUrl ? (
            <div className="flex items-center justify-center rounded-lg bg-grey-100 p-3">
              <img
                src={displayUrl}
                alt={label}
                className="max-h-[60vh] max-w-full rounded-md object-contain"
              />
            </div>
          ) : (
            <div className="flex h-40 items-center justify-center rounded-lg bg-grey-100 text-text-secondary">
              <ImageIcon className="size-8" aria-hidden />
            </div>
          )}
          <DialogFooter>
            {readOnly ? (
              // Read-only preview — Close-only footer. No Remove / Change.
              <div className="flex w-full justify-end">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setPreviewOpen(false)}
                >
                  Close
                </Button>
              </div>
            ) : (
              <div
                className={cn(
                  'flex w-full items-center',
                  hideRemove ? 'justify-end' : 'justify-between',
                )}
              >
                {!hideRemove && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleRemove}
                    disabled={!displayUrl}
                  >
                    Remove
                  </Button>
                )}
                <PositiveNegativeButtons
                  negativeLabel="Close"
                  positiveLabel="Change"
                  onNegative={() => setPreviewOpen(false)}
                  onPositive={openFilePicker}
                />
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
